import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDebug } from '../contexts/DebugContext';
import { can } from '../utils/permissions';
import { displayDate } from '../utils/dates';
import { PageHeader, Banner, IconButton, DebugPanel, ICONS, JobPageLink } from './ui';
import InvoiceExtras from './InvoiceExtras';
import api from '../services/api';

function money(v) {
  const n = parseFloat(v);
  if (isNaN(n) || n === 0) return '';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
function dateOnly(v) {
  return (v || '').toString().slice(0, 10);
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Effective moment to post: for today, include the current time so same-day
// entries order correctly; for a past date, time is irrelevant (noon local).
function effectiveMoment(dateStr) {
  if (dateStr === todayStr()) return new Date().toISOString();
  return new Date(`${dateStr}T12:00:00`).toISOString();
}
function joinDesc(e) {
  return [e.Description1, e.Description2, e.Description3, e.Description4].filter((s) => s && s.trim()).join('');
}

// Aging: bucket an invoice's outstanding balance by the number of days since its
// Posting Date relative to today, in 30-day periods — matching Report 50041
// "Aged Accts. Receivable by Job" aged by transaction (posting) date.
//
// Default columns:  Current (0-30), 31-60, 61-90, 90+.
// "Hide <60 days" (aged) columns: 61-90, 91-120, 121-150, 151+ — everything 60
// days or newer is dropped entirely, so only genuinely aged (61+) invoices show.
const AG_COLS_DEFAULT = [
  { key: 'current', label: 'Current' },
  { key: 'd3160', label: '31–60' },
  { key: 'd6190', label: '61–90' },
  { key: 'd90', label: '90+' },
];
const AG_COLS_AGED = [
  { key: 'd6190', label: '61–90' },
  { key: 'd91120', label: '91–120' },
  { key: 'd121150', label: '121–150' },
  { key: 'd151', label: '151+' },
];

// Days since the invoice's posting date (the report's Trans Date aging basis).
function daysAged(postingDate) {
  if (!postingDate) return 0;
  const d = new Date(dateOnly(postingDate) + 'T00:00:00');
  if (isNaN(d.getTime())) return 0;
  const today = new Date(todayStr() + 'T00:00:00');
  return Math.floor((today - d) / 86400000);
}
// An invoice is shown in aged (Hide <60) mode only when it is more than 60 days
// old; everything 60 days or newer is dropped (report's k=0 case).
function isAged(inv) {
  return daysAged(inv.PostingDate) > 60;
}
function bucketKey(n, aged) {
  if (aged) {
    if (n <= 60) return null;        // dropped in aged view
    if (n <= 90) return 'd6190';
    if (n <= 120) return 'd91120';
    if (n <= 150) return 'd121150';
    return 'd151';
  }
  if (n <= 30) return 'current';
  if (n <= 60) return 'd3160';
  if (n <= 90) return 'd6190';
  return 'd90';
}
function agingOf(inv, aged) {
  const amt = parseFloat(inv.OutstandingBalance) || 0;
  const key = bucketKey(daysAged(inv.PostingDate), aged);
  const out = {};
  if (key) out[key] = amt;
  return out;
}
function sumAging(list, aged) {
  const out = {};
  for (const inv of list || []) {
    const a = agingOf(inv, aged);
    for (const k in a) out[k] = (out[k] || 0) + a[k];
  }
  return out;
}

const CELL_W = 78;
function AgingCells({ totals, cols, bold }) {
  return (
    <div style={{ display: 'flex', flexShrink: 0 }}>
      {cols.map((c) => (
        <span key={c.key} style={{ width: CELL_W, textAlign: 'right', fontSize: '12px', fontWeight: bold ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}>
          {money(totals[c.key] || 0)}
        </span>
      ))}
    </div>
  );
}

function debugFrom(source) {
  if (!source) return [];
  const body = source._navDebug ? source : (source.responseBody || null);
  const arr = body && body._navDebug;
  return Array.isArray(arr) ? arr : [];
}

export default function InvoicesView({ deepLinkJob, deepLinkCustomer }) {
  const { user } = useAuth();
  const { debugMode } = useDebug();
  const accounting = can(user, 'receivablesEdit');
  // Invoice numbers are shown as "#123456" unless this user's admin setting
  // turns the prefix off.
  const invNo = (n) => (user && user.hideInvoicePrefix ? '' : '#') + n;

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navDebug, setNavDebug] = useState([]);
  const [hideRecent, setHideRecent] = useState(false);
  const aged = hideRecent;
  const activeCols = aged ? AG_COLS_AGED : AG_COLS_DEFAULT;

  // Active filter, seeded from a ?job= or ?customer= deep link. A customer
  // filter shows all jobs under that customer (including child customers).
  // "Remove Filter" clears it so the user sees the full set they'd normally get.
  // Re-seeded if the deep link itself changes.
  const seedFilter = () => (
    deepLinkJob ? { type: 'job', value: deepLinkJob }
      : deepLinkCustomer ? { type: 'customer', value: deepLinkCustomer }
      : null
  );
  const [filter, setFilter] = useState(seedFilter);
  useEffect(() => {
    setFilter(deepLinkJob ? { type: 'job', value: deepLinkJob }
      : deepLinkCustomer ? { type: 'customer', value: deepLinkCustomer }
      : null);
  }, [deepLinkJob, deepLinkCustomer]);

  const [expCust, setExpCust] = useState({});        // parentKey -> true (expanded)
  const [expJob, setExpJob] = useState({});          // `${parentKey}|${jobNo}` -> true
  const [expanded, setExpanded] = useState({});      // invoiceNo -> true (status log)
  const [logs, setLogs] = useState({});              // invoiceNo -> entries[]
  const [logLoading, setLogLoading] = useState({});  // invoiceNo -> bool
  const [docBusy, setDocBusy] = useState({});        // invoiceNo -> bool
  const [receiptBusy, setReceiptBusy] = useState({}); // invoiceNo -> bool

  const [modal, setModal] = useState(null);          // { kind:'status'|'revision', invoiceNo }
  const [modalText, setModalText] = useState('');
  const [modalDate, setModalDate] = useState(todayStr());
  const [modalFile, setModalFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [extraInvoices, setExtraInvoices] = useState([]); // additional invoice rows a status is also posted to
  const [invSearch, setInvSearch] = useState('');         // search box in the Post Status picker
  const fileInputRef = useRef(null);

  const showDebug = useCallback((source) => { setNavDebug(debugFrom(source)); }, []);
  const addDebug = useCallback((source) => {
    const calls = debugFrom(source);
    if (calls.length) setNavDebug((prev) => [...prev, ...calls]);
  }, []);

  const jobFilterVal = filter && filter.type === 'job' ? filter.value : '';
  const custFilterVal = filter && filter.type === 'customer' ? filter.value : '';

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.getInvoices(jobFilterVal, custFilterVal, debugMode)
      .then((r) => { setRows((r && r.invoices) || []); showDebug(r); })
      .catch((e) => { setError(e.message || 'Failed to load invoices'); showDebug(e); })
      .finally(() => setLoading(false));
  }, [jobFilterVal, custFilterVal, debugMode, showDebug]);

  useEffect(() => { load(); }, [load]);

  // Group rows: parent customer -> job -> invoices. When hideRecent is on, drop
  // every invoice 60 days or newer, then any job/customer left with none; totals
  // reflect only the shown (aged) invoices.
  const groups = useMemo(() => {
    const byParent = new Map();
    for (const r of rows || []) {
      const pKey = r.ParentCustomerNo || r.CustomerNo || '';
      if (!byParent.has(pKey)) {
        byParent.set(pKey, { key: pKey, name: r.ParentCustomerName || r.CustomerName || pKey, jobs: new Map() });
      }
      const p = byParent.get(pKey);
      const jKey = r.JobNo || '';
      if (!p.jobs.has(jKey)) {
        p.jobs.set(jKey, { jobNo: r.JobNo, jobDescription: r.JobDescription, invoices: [] });
      }
      p.jobs.get(jKey).invoices.push(r);
    }
    let list = Array.from(byParent.values()).map((p) => {
      let jobs = Array.from(p.jobs.values()).map((j) => {
        const invoices = (hideRecent ? j.invoices.filter(isAged) : j.invoices)
          .slice()
          .sort((a, b) => String(a.InvoiceNo || '').localeCompare(String(b.InvoiceNo || ''), undefined, { numeric: true, sensitivity: 'base' }));
        return { ...j, invoices, totals: sumAging(invoices, aged) };
      });
      if (hideRecent) jobs = jobs.filter((j) => j.invoices.length > 0);
      jobs.sort((a, b) => String(a.jobNo || '').localeCompare(String(b.jobNo || '')));
      const shown = jobs.flatMap((j) => j.invoices);
      return { ...p, jobs, totals: sumAging(shown, aged) };
    });
    if (hideRecent) list = list.filter((p) => p.jobs.length > 0);
    return list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [rows, hideRecent, aged]);

  // Default all collapsed. A job filter expands its customer and that job so the
  // target is visible; a customer filter expands the matching customer group(s)
  // so all their jobs show.
  useEffect(() => {
    if (!groups.length) { setExpCust({}); setExpJob({}); return; }
    if (jobFilterVal) {
      for (const p of groups) {
        const j = p.jobs.find((x) => String(x.jobNo) === String(jobFilterVal));
        if (j) { setExpCust({ [p.key]: true }); setExpJob({ [`${p.key}|${j.jobNo}`]: true }); return; }
      }
    }
    if (custFilterVal) {
      const cust = {};
      groups.forEach((p) => { cust[p.key] = true; });
      setExpCust(cust);
      setExpJob({});
      return;
    }
    setExpCust({});
    setExpJob({});
  }, [groups, jobFilterVal, custFilterVal]);

  // Expand every customer AND job (but not invoices or their status logs);
  // collapse everything.
  const expandAll = () => {
    const cust = {}; const jobs = {};
    groups.forEach((p) => {
      cust[p.key] = true;
      p.jobs.forEach((j) => { jobs[`${p.key}|${j.jobNo}`] = true; });
    });
    setExpCust(cust);
    setExpJob(jobs);
  };
  const collapseAll = () => {
    setExpCust({});
    setExpJob({});
    setExpanded({});
  };

  const grandTotals = useMemo(() => {
    const shown = groups.flatMap((p) => p.jobs.flatMap((j) => j.invoices));
    return sumAging(shown, aged);
  }, [groups, aged]);

  const refreshLog = useCallback(async (invoiceNo) => {
    setLogLoading((s) => ({ ...s, [invoiceNo]: true }));
    try {
      const r = await api.getInvoiceStatusLog(invoiceNo, debugMode);
      addDebug(r);
      setLogs((s) => ({ ...s, [invoiceNo]: (r && r.entries) || [] }));
    } catch (e) {
      addDebug(e);
      setLogs((s) => ({ ...s, [invoiceNo]: [] }));
    } finally {
      setLogLoading((s) => ({ ...s, [invoiceNo]: false }));
    }
  }, [debugMode, addDebug]);

  const toggleExpand = async (invoiceNo) => {
    const open = !expanded[invoiceNo];
    setExpanded((s) => ({ ...s, [invoiceNo]: open }));
    if (open && !logs[invoiceNo]) await refreshLog(invoiceNo);
  };

  // Refresh: re-fetch the invoice list AND invalidate the cached status logs so
  // status/revisions posted elsewhere (e.g. the Outlook add-in) are pulled in.
  // Any currently-expanded invoice's log is re-fetched immediately.
  const handleRefresh = useCallback(() => {
    const openInvoices = Object.keys(expanded).filter((k) => expanded[k]);
    setLogs({});
    load();
    openInvoices.forEach((invoiceNo) => refreshLog(invoiceNo));
  }, [expanded, load, refreshLog]);

  const openDocImage = async (invoiceNo) => {
    setDocBusy((s) => ({ ...s, [invoiceNo]: true }));
    try {
      const r = await api.getInvoiceDocImage(invoiceNo, debugMode);
      addDebug(r);
      if (r && r.link) window.open(r.link, '_blank', 'noopener');
      else alert('No invoice document found for this invoice.');
    } catch (e) {
      addDebug(e);
      alert(e.message || 'Failed to open DocImage');
    } finally {
      setDocBusy((s) => ({ ...s, [invoiceNo]: false }));
    }
  };

  // Generate the invoice's receipt report and append the receipts not already
  // present as a new revision, then open the combined PDF. Receipts already
  // attached (tracked by a hidden code in the invoice PDF) are not duplicated.
  const addReceipts = async (invoiceNo, jobNo) => {
    setReceiptBusy((s) => ({ ...s, [invoiceNo]: true }));
    try {
      const r = await api.addInvoiceReceipts(invoiceNo, jobNo, debugMode);
      addDebug(r);
      const added = (r && typeof r.added === 'number') ? r.added : 0;
      if (added > 0) {
        alert(`${added} receipt(s) added to invoice ${invoiceNo}.`);
        setExpanded((s) => ({ ...s, [invoiceNo]: true }));
        await refreshLog(invoiceNo);
      } else {
        alert((r && r.message) || 'All receipts are already attached to this invoice. Nothing was added.');
      }
      if (r && r.link) window.open(r.link, '_blank', 'noopener');
    } catch (e) {
      addDebug(e);
      alert(e.message || 'Failed to add receipts');
    } finally {
      setReceiptBusy((s) => ({ ...s, [invoiceNo]: false }));
    }
  };

  const openModal = (kind, invoiceNo, jobNo) => {
    setModal({ kind, invoiceNo, jobNo });
    setModalText(kind === 'revision' ? `Invoice Revised ${todayStr()}` : '');
    setModalDate(todayStr());
    setModalFile(null);
    setDragOver(false);
    setModalError(null);
    setExtraInvoices([]);
    setInvSearch('');
  };

  // Post Status "also post to" picker: add/remove additional target invoices.
  const addExtraInvoice = (row) =>
    setExtraInvoices((list) => (list.some((x) => x.InvoiceNo === row.InvoiceNo) ? list : [...list, row]));
  const removeExtraInvoice = (invoiceNo) =>
    setExtraInvoices((list) => list.filter((x) => x.InvoiceNo !== invoiceNo));

  const submitModal = async () => {
    if (!modal) return;
    const invoiceNo = modal.invoiceNo;
    if (modal.kind === 'status' && !modalText.trim()) { setModalError('Please enter a status.'); return; }
    if (modal.kind === 'revision' && !modalFile) { setModalError('Please attach the revised invoice file.'); return; }
    if (modal.kind === 'extraPages' && !modalFile) { setModalError('Please attach the file with the extra pages.'); return; }
    setModalBusy(true);
    setModalError(null);
    try {
      const eff = effectiveMoment(modalDate);
      const jobNo = modal.jobNo;
      if (modal.kind === 'status') {
        // Post the same status to the primary invoice plus any additional
        // invoices the user added. Post each independently so one failure does
        // not prevent the rest; report any that failed.
        const text = modalText.trim();
        const targets = [invoiceNo, ...extraInvoices.map((x) => x.InvoiceNo)];
        const failures = [];
        let lastDebug = null;
        for (const no of targets) {
          try {
            lastDebug = await api.postInvoiceStatus(no, text, eff, debugMode);
          } catch (e) {
            lastDebug = e;
            failures.push(`${invNo(no)}: ${e.message || 'failed'}`);
          }
        }
        setNavDebug(debugFrom(lastDebug));
        if (failures.length) {
          setModalError(`Posted ${targets.length - failures.length} of ${targets.length}. Failed — ${failures.join('; ')}`);
          return;
        }
        setModal(null);
        for (const no of targets) {
          setExpanded((s) => ({ ...s, [no]: true }));
          await refreshLog(no);
        }
        return;
      }
      let r;
      if (modal.kind === 'revision') r = await api.postInvoiceRevision(invoiceNo, modalFile, modalText.trim(), eff, jobNo, debugMode);
      else r = await api.postInvoiceExtraPages(invoiceNo, modalFile, jobNo, debugMode);
      setNavDebug(debugFrom(r));
      setModal(null);
      setExpanded((s) => ({ ...s, [invoiceNo]: true }));
      await refreshLog(invoiceNo);
    } catch (e) {
      setModalError(e.message || 'Failed to post');
      setNavDebug(debugFrom(e));
    } finally {
      setModalBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) setModalFile(f);
  };

  const debugPanel = <DebugPanel calls={navDebug} />;

  if (loading) return <div className="loading"><div className="spinner" />Loading invoices...</div>;
  if (error) {
    return (
      <div style={{ padding: '16px', maxWidth: '1120px', margin: '0 auto' }}>
        <Banner kind="err">
          {error}
          <div style={{ marginTop: '8px' }}>
            <button className="btn btn-outline btn-sm" onClick={load}>Retry</button>
          </div>
        </Banner>
        {debugPanel}
      </div>
    );
  }

  // Column-label legend, right-aligned to line up with the aging cells.
  const legend = (
    <div style={{ display: 'flex', alignItems: 'flex-end', padding: '0 10px 4px', gap: '8px' }}>
      <span style={{ flex: 1 }} />
      <div style={{ display: 'flex', flexShrink: 0 }}>
        {activeCols.map((c) => (
          <span key={c.key} style={{ width: CELL_W, textAlign: 'right', fontSize: '11px', fontWeight: 600, color: 'var(--gray-500)' }}>{c.label}</span>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '12px 8px 32px', maxWidth: '1120px', margin: '0 auto' }}>
      <PageHeader
        title={`Receivables${filter ? ` — ${filter.type === 'job' ? 'Job' : 'Customer'} ${filter.value}` : ''}`}
        onRefresh={handleRefresh}
        actions={(
          <>
            {filter && (
              <button className="btn btn-outline btn-sm" onClick={() => setFilter(null)} title="Show all customers and jobs you have access to">
                Remove Filter
              </button>
            )}
            <IconButton icon="expandAll" title="Expand all" onClick={expandAll} disabled={!groups.length} />
            <IconButton icon="collapseAll" title="Collapse all" onClick={collapseAll} disabled={!groups.length} />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', userSelect: 'none', margin: '0 4px' }}>
              <input type="checkbox" checked={hideRecent} onChange={(e) => setHideRecent(e.target.checked)} />
              61+ only
            </label>
            <a href="/help-invoicing" target="_blank" rel="noreferrer" title="Receivables help" aria-label="Receivables help"
              className="icon-btn" style={{ textDecoration: 'none' }}>{ICONS.help}</a>
          </>
        )}
      />

      {debugPanel}

      {groups.length === 0 ? (
        <div className="empty-state"><p>No outstanding invoices.</p></div>
      ) : (
        <>
          {legend}
          {groups.map((p) => {
            const custOpen = !!expCust[p.key];
            return (
              <div key={p.key} style={{ marginBottom: '10px', border: '1px solid var(--gray-200)', borderRadius: '6px', overflow: 'hidden' }}>
                <div
                  onClick={() => setExpCust((s) => ({ ...s, [p.key]: !s[p.key] }))}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', background: '#e5edff', padding: '7px 10px' }}>
                  <span style={{ width: '14px', fontSize: '11px', color: 'var(--gray-500)' }}>{custOpen ? '▼' : '▶'}</span>
                  <span style={{ flex: 1 }}>
                    {p.name} <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>({p.key})</span>
                  </span>
                  <AgingCells totals={p.totals} cols={activeCols} bold />
                </div>

                {custOpen && p.jobs.map((j) => {
                  const jKey = `${p.key}|${j.jobNo}`;
                  const jobOpen = !!expJob[jKey];
                  return (
                    <div key={j.jobNo || '_'} style={{ borderTop: '1px solid var(--gray-100)' }}>
                      <div
                        onClick={() => setExpJob((s) => ({ ...s, [jKey]: !s[jKey] }))}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', background: 'var(--gray-50)', padding: '5px 10px 5px 24px' }}>
                        <span style={{ width: '14px', fontSize: '10px', color: 'var(--gray-500)' }}>{jobOpen ? '▼' : '▶'}</span>
                        <span style={{ flex: 1 }}>{j.jobNo || '(no job)'}<JobPageLink jobNo={j.jobNo} />{j.jobDescription ? ` — ${j.jobDescription}` : ''}</span>
                        <AgingCells totals={j.totals} cols={activeCols} />
                      </div>

                      {jobOpen && j.invoices.map((inv) => (
                        <div key={inv.InvoiceNo} style={{ borderTop: '1px solid var(--gray-100)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px 6px 30px', flexWrap: 'nowrap' }}>
                            <button onClick={() => toggleExpand(inv.InvoiceNo)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: 'var(--gray-500)', width: '14px', flexShrink: 0 }}
                              title="Show status log">
                              {expanded[inv.InvoiceNo] ? '▼' : '▶'}
                            </button>
                            <span style={{ fontWeight: 600, fontSize: '13px', flexShrink: 0 }}>{invNo(inv.InvoiceNo)}<InvoiceExtras invoiceNo={inv.InvoiceNo} jobNo={inv.JobNo} posted /></span>
                            <span style={{ fontSize: '13px', minWidth: '90px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{money(inv.OutstandingBalance)}</span>
                            <span style={{ fontSize: '12px', color: 'var(--gray-500)', flexShrink: 0 }} title="Posting date">{displayDate(inv.PostingDate)}</span>
                            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                              <button className="btn btn-outline btn-sm" disabled={docBusy[inv.InvoiceNo]} onClick={() => openDocImage(inv.InvoiceNo)}>
                                {docBusy[inv.InvoiceNo] ? '…' : 'View Inv'}
                              </button>
                              <button className="btn btn-outline btn-sm" onClick={() => openModal('status', inv.InvoiceNo, inv.JobNo)}>Post Status</button>
                              {accounting && (
                                <button className="btn btn-outline btn-sm" onClick={() => openModal('revision', inv.InvoiceNo, inv.JobNo)}>Post Revision</button>
                              )}
                              {/* Add Pages button hidden (kept for future re-enable): change `false` back to `accounting`. */}
                              {false && accounting && (
                                <button className="btn btn-outline btn-sm" onClick={() => openModal('extraPages', inv.InvoiceNo, inv.JobNo)}>Add Pages</button>
                              )}
                              {accounting && (
                                <button className="btn btn-outline btn-sm" disabled={receiptBusy[inv.InvoiceNo]} onClick={() => addReceipts(inv.InvoiceNo, inv.JobNo)}>
                                  {receiptBusy[inv.InvoiceNo] ? 'Adding…' : 'Add Receipts'}
                                </button>
                              )}
                            </div>
                          </div>
                          {expanded[inv.InvoiceNo] && (
                            <div style={{ padding: '4px 10px 10px 62px', background: '#fbfbfd' }}>
                              {logLoading[inv.InvoiceNo] ? (
                                <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>Loading status log…</div>
                              ) : (logs[inv.InvoiceNo] || []).length === 0 ? (
                                <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>No status log entries.</div>
                              ) : (
                                [...(logs[inv.InvoiceNo] || [])]
                                  .sort((a, b) => new Date(b.EffectiveDateTime || 0) - new Date(a.EffectiveDateTime || 0))
                                  .map((e) => (
                                    <div key={e.EntryNo} style={{ fontSize: '12px', padding: '4px 0', borderBottom: '1px solid var(--gray-100)' }}>
                                      <span style={{ color: 'var(--gray-500)' }}>{displayDate(e.EffectiveDateTime)}</span>
                                      {' · '}
                                      <span style={{ color: 'var(--gray-500)' }}>{e.UserId}</span>
                                      {' · '}
                                      <span>{joinDesc(e) || '(no description)'}</span>
                                      {e.Link ? (
                                        <>{' '}<a href={e.Link} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>open link</a></>
                                      ) : null}
                                    </div>
                                  ))
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Grand total across all listed customers */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderTop: '2px solid var(--gray-300)', fontWeight: 700, fontSize: '13px' }}>
            <span style={{ flex: 1 }}>Total</span>
            <AgingCells totals={grandTotals} cols={activeCols} bold />
          </div>
        </>
      )}

      {modal && (
        <div className="modal-overlay">
          {/* No click-to-close on the backdrop: a stray click outside must not
              discard what the user has typed. Close via Cancel or the × only. */}
          <div className="modal-content" style={{ maxWidth: '460px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong>{modal.kind === 'status' ? 'Post Status' : modal.kind === 'revision' ? 'Post Revision' : 'Post Extra Pages'} — {invNo(modal.invoiceNo)}{modal.kind === 'status' && extraInvoices.length > 0 ? ` (+${extraInvoices.length} more)` : ''}</strong>
              <IconButton icon="close" title="Close" onClick={() => !modalBusy && setModal(null)} />
            </div>

            {(modal.kind === 'revision' || modal.kind === 'extraPages') && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{modal.kind === 'revision' ? 'Revised invoice file' : 'File with extra pages'}</label>
                <div
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  style={{
                    marginTop: '4px', border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--gray-300)'}`,
                    borderRadius: '8px', padding: '16px', textAlign: 'center', cursor: 'pointer',
                    background: dragOver ? '#eff6ff' : 'var(--gray-50)', fontSize: '13px', color: 'var(--gray-600)',
                  }}>
                  {modalFile ? (
                    <span><strong>{modalFile.name}</strong> ({Math.round(modalFile.size / 1024)} KB) — click to replace</span>
                  ) : (
                    <span>{modal.kind === 'revision' ? 'Drag & drop the revised invoice here, or click to select' : 'Drag & drop the extra pages here, or click to select'}</span>
                  )}
                </div>
                <input ref={fileInputRef} type="file" style={{ display: 'none' }}
                  onChange={(e) => setModalFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
              </div>
            )}

            {modal.kind === 'extraPages' ? (
              <div style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                The uploaded pages will be appended to the invoice's latest document image and saved as a new document image, effective now.
              </div>
            ) : (
              <>
                <label style={{ fontSize: '11px', color: 'var(--gray-500)' }}>{modal.kind === 'status' ? 'Status' : 'Note'}</label>
                <textarea className="form-control" rows={3} value={modalText} onChange={(e) => setModalText(e.target.value)} maxLength={1000} />
                <label style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '8px', display: 'block' }}>Effective date</label>
                <input className="form-control" type="date" value={modalDate} onChange={(e) => setModalDate(e.target.value)} />

                {modal.kind === 'status' && (() => {
                  const selectedNos = new Set([modal.invoiceNo, ...extraInvoices.map((x) => x.InvoiceNo)]);
                  const sameJob = (rows || []).filter((r) =>
                    r.JobNo && modal.jobNo && String(r.JobNo) === String(modal.jobNo) && !selectedNos.has(r.InvoiceNo));
                  const q = invSearch.trim().toLowerCase();
                  const results = q
                    ? (rows || []).filter((r) => !selectedNos.has(r.InvoiceNo) && (
                        String(r.InvoiceNo || '').toLowerCase().includes(q) ||
                        String(r.CustomerName || '').toLowerCase().includes(q) ||
                        String(r.ParentCustomerName || '').toLowerCase().includes(q) ||
                        String(r.JobDescription || '').toLowerCase().includes(q) ||
                        String(r.JobNo || '').toLowerCase().includes(q)
                      )).slice(0, 12)
                    : [];
                  const label = (r) => `${invNo(r.InvoiceNo)}${r.CustomerName || r.ParentCustomerName ? ` · ${r.CustomerName || r.ParentCustomerName}` : ''}${r.JobNo ? ` · ${r.JobNo}` : ''}`;
                  const addRowStyle = {
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
                    padding: '4px 6px', fontSize: '12px', cursor: 'pointer', borderRadius: '6px',
                  };
                  return (
                    <div style={{ marginTop: '12px', borderTop: '1px solid var(--gray-100)', paddingTop: '10px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--gray-500)', display: 'block' }}>
                        Also post this status to (optional)
                      </label>

                      {extraInvoices.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '6px 0' }}>
                          {extraInvoices.map((r) => (
                            <span key={r.InvoiceNo} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px',
                              background: 'var(--gray-100)', borderRadius: '12px', padding: '2px 8px',
                            }}>
                              {invNo(r.InvoiceNo)}
                              <button type="button" onClick={() => removeExtraInvoice(r.InvoiceNo)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', fontSize: '13px', lineHeight: 1 }}
                                title="Remove" aria-label="Remove">{ICONS.close}</button>
                            </span>
                          ))}
                        </div>
                      )}

                      {sameJob.length > 0 && (
                        <div style={{ marginTop: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>Other invoices on this job</span>
                            <button type="button" className="btn btn-outline btn-sm" onClick={() => sameJob.forEach(addExtraInvoice)}>
                              Add all
                            </button>
                          </div>
                          <div style={{ maxHeight: '110px', overflowY: 'auto', marginTop: '4px' }}>
                            {sameJob.map((r) => (
                              <div key={r.InvoiceNo} style={addRowStyle} onClick={() => addExtraInvoice(r)}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gray-50)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                                <span>{label(r)}</span>
                                <span style={{ color: 'var(--primary)', fontSize: '13px' }}>+ Add</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <input className="form-control" style={{ marginTop: '8px' }}
                        placeholder="Search by invoice #, customer, or job name"
                        value={invSearch} onChange={(e) => setInvSearch(e.target.value)} />
                      {q && (
                        <div style={{ maxHeight: '150px', overflowY: 'auto', marginTop: '4px', border: '1px solid var(--gray-100)', borderRadius: '6px' }}>
                          {results.length === 0 ? (
                            <div style={{ padding: '6px', fontSize: '12px', color: 'var(--gray-500)' }}>No matching invoices.</div>
                          ) : results.map((r) => (
                            <div key={r.InvoiceNo} style={addRowStyle} onClick={() => addExtraInvoice(r)}
                              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gray-50)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                              <span>{label(r)}</span>
                              <span style={{ color: 'var(--primary)', fontSize: '13px' }}>+ Add</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
            <Banner kind="err">{modalError}</Banner>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
              <button className="btn btn-outline" onClick={() => setModal(null)} disabled={modalBusy}>Cancel</button>
              <button className="btn btn-primary" onClick={submitModal} disabled={modalBusy}>{modalBusy ? 'Posting…' : (modal.kind === 'status' && extraInvoices.length > 0 ? `Post to ${extraInvoices.length + 1}` : 'Post')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
